/**
 * How a user's security state reads on the card.
 *
 * @module Pages/Settings/UserManagement
 */

import type { User } from '../Types/UserManagement.types';
import type { Status } from '@/Shared/components/StatusTag/StatusTag';

/** Failed attempts at or above this are shown in red; the BFF locks the account at 5. */
export const FAILED_ATTEMPTS_WARNING_THRESHOLD = 3;

/** Account state, derived from `lockedUntil`. */
export interface AccountState {
  status: Status;
  label: string;
}

/**
 * Whether the account is locked right now.
 *
 * `lockedUntil` stays populated after a lock expires, so a past date means the account is usable
 * again — reading the field alone would keep showing a stale "Bloqueada".
 */
export function getAccountState(user: Pick<User, 'lockedUntil'>, now = new Date()): AccountState {
  if (!user.lockedUntil) return { status: 'success', label: 'Ativa' };

  const lockedUntil = new Date(user.lockedUntil);
  if (Number.isNaN(lockedUntil.getTime())) return { status: 'success', label: 'Ativa' };

  return lockedUntil > now
    ? { status: 'danger', label: 'Bloqueada' }
    : { status: 'success', label: 'Ativa' };
}

/** Whether the failed-attempt count deserves the red treatment. */
export function isFailedAttemptsAlarming(failedLoginAttempts: number | undefined): boolean {
  return (failedLoginAttempts ?? 0) > FAILED_ATTEMPTS_WARNING_THRESHOLD;
}

/**
 * Short date, or a dash when there is nothing to show.
 *
 * A user who has never signed in has a null `lastLoginAt`; "Nunca" says that plainly instead of
 * leaving the field blank.
 */
export function formatUserDate(value: string | null | undefined, emptyLabel = '—'): string {
  if (!value) return emptyLabel;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return emptyLabel;

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Filters users by name or e-mail.
 *
 * Client-side because `GET /v1/admin/users` has no search parameter and returns everyone in one
 * response — the whole list is already in memory, so there is nothing to ask the server for.
 *
 * Accent-insensitive: someone looking for "barbara" should find "Bárbara", and nobody types the
 * accent into a search box.
 */
export function filterUsers<T extends { name?: string | null; email: string }>(
  users: T[],
  term: string,
): T[] {
  const needle = normalizeForSearch(term);
  if (!needle) return users;

  return users.filter(user =>
    `${normalizeForSearch(user.name ?? '')} ${normalizeForSearch(user.email)}`.includes(needle),
  );
}

/** Lowercased and stripped of accents, so "Bárbara" and "barbara" are the same string. */
function normalizeForSearch(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Splits the listing into the people who can log in and the ones who were deactivated.
 *
 * `isActive` is optional on purpose: a BFF that does not send it yet is read as active, so an
 * older deployment shows everyone under Ativos instead of reporting that the whole company was
 * deactivated at once.
 */
export function splitUsersByStatus<T extends { isActive?: boolean }>(
  users: T[],
): { active: T[]; inactive: T[] } {
  return {
    active: users.filter(user => user.isActive !== false),
    inactive: users.filter(user => user.isActive === false),
  };
}

/**
 * How each role reads on screen, in Portuguese.
 *
 * The BFF names the roles in English because they are also permission keys; the panel is written
 * in Portuguese, so the slug never reaches the user by itself. A role missing from here still
 * renders — see {@link formatRoleName} — which is what keeps a role created outside the seed from
 * showing up blank.
 */
export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super admin',
  admin: 'Administrador',
  /*
    Os dois de pesquisa diferem em UMA coisa, e é a destrutiva: o segundo aprova a própria
    pesquisa e apaga respostas coletadas e dados de uso. O rótulo carrega isso porque é aqui que
    a escolha acontece — quem estiver convidando alguém não vai abrir a doc do RBAC para
    descobrir a diferença entre "gestor" e "administrador".
  */
  research_manager: 'Gestor de pesquisas',
  super_research_admin: 'Administrador de pesquisas (apaga respostas)',
  signature_manager: 'Gestor de assinaturas',
  signature_reader: 'Consulta de assinaturas',
};

/**
 * The roles a person can be given, out of everything the API returns.
 *
 * The BFF can hold roles this product does not define — `platform_preferences_manager` and
 * `platform_preferences_reader` exist in production, created straight in the database and named by
 * no seed or script here. Offering them in a picker invites someone to hand out access this panel
 * cannot describe, let alone revoke knowingly.
 *
 * Both pickers — creating a user and changing their role — read the list through here, so they
 * cannot drift apart again.
 *
 * Roles a user *already holds* are a separate matter: see the callers, which preserve them rather
 * than stripping what they do not show.
 */
export function selectableRoles<T extends { name: string }>(roles: T[]): T[] {
  return roles.filter(role => role.name in ROLE_LABELS);
}

/** The role's name in Portuguese, falling back to `email_manager` → `Email manager`. */
export function formatRoleName(name: string): string {
  const label = ROLE_LABELS[name];
  if (label) return label;

  const spaced = name.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
